import React, { createContext, useState, useContext, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { getUserProfile } from "../services/api";
import { jwtUtils } from "../utils/tokenManager";
import logger from "../utils/logger";

const AuthContext = createContext(null);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const checkAuth = useCallback(async () => {
    logger.debug("Checking authentication state");
    setLoading(true); // Set loading to true while checking
    
    const token = localStorage.getItem("authToken");
    
    if (!token) {
      logger.debug("No authentication token found");
      setUser(null);
      setError(null); // Clear any previous errors
      setLoading(false);
      return;
    }
    
    // Check if token is expired
    if (jwtUtils.isExpired(token)) {
      if (process.env.NODE_ENV !== 'production') console.info("AuthProvider: Token is expired, removing"); // Debug log
      localStorage.removeItem("authToken");
      setUser(null);
      setError({ message: "Your session has expired. Please log in again." });
      setLoading(false);
      return;
    }
    
    // Log token expiration time
    const timeRemaining = jwtUtils.timeRemaining(token);
    logger.debug(`Token valid for ${timeRemaining} seconds`, { timeRemaining })
    
    try {
      const profile = await getUserProfile();
      setUser(profile);
      setError(null); // Clear any previous errors
      logger.debug("User authentication successful", { userId: profile._id, email: profile.email })
    } catch (error) {
      logger.error("Authentication check failed", { 
        error: error.message,
        stack: error.stack 
      });
      
      // If the error is authentication-related, clear the token
      if (error.message.includes("authentication") || 
          error.message.includes("Not authenticated") ||
          error.message.includes("Unauthorized")) {
        logger.auth("Removing invalid token", { reason: error.message });
        localStorage.removeItem("authToken");
        setUser(null);
      }
      
      setError(error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial auth check on mount
  useEffect(() => {
    checkAuth();
  }, []);
  
  // Listen for localStorage changes (for multi-tab support)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "authToken") {
        logger.debug("Auth token changed in localStorage", {
          tokenChanged: true,
          tokenAdded: !!e.newValue
        });
        if (e.newValue) {
          // Token added or changed
          logger.auth("Token updated in another tab/window");
          checkAuth();
        } else {
          // Token removed
          logger.auth("Token removed in another tab/window");
          setUser(null);
        }
      }
    };
    
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);
  
  // Periodically check token expiration (every 5 minutes)
  useEffect(() => {
    const checkTokenExpiration = () => {
      const token = localStorage.getItem("authToken");
      if (token && jwtUtils.isExpired(token)) {
        logger.auth("Token expired during periodic check", {
          timeRemaining: jwtUtils.timeRemaining(token)
        });
        localStorage.removeItem("authToken");
        setUser(null);
        setError({ message: "Your session has expired. Please log in again." });
      }
    };
    
    const intervalId = setInterval(checkTokenExpiration, 5 * 60 * 1000); // 5 minutes
    
    return () => clearInterval(intervalId);
  }, []);

  const logout = () => {
    localStorage.removeItem("authToken");
    setUser(null);
    logger.auth("User logged out", { userId: user?._id });
  };
  
  // Method to manually refresh auth state
  const refreshAuth = () => {
    logger.debug("Manual authentication refresh requested", { userId: user?._id });
    checkAuth();
  };

  const value = {
    user,
    setUser,
    loading,
    logout,
    refreshAuth,
    error,
  };

  // Use useEffect for logging to avoid triggering renders
  useEffect(() => {
    logger.component("AuthProvider", "state update", { loading, authenticated: !!user });
  }, [loading, user]);

  if (loading) {
    logger.debug("AuthProvider showing loading state");
    return (
      <div className="min-h-screen flex items-center justify-center">
        Loading...
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center text-red-600">
        Error: {error.message}
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export { AuthProvider, useAuth };
